import React from 'react';
import { ChevronDown } from 'lucide-react';
interface BaseInputProps {
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
}
export function FieldWrapper({
  label,
  required,
  error,
  helperText,
  className,
  children
}: BaseInputProps & {
  children: React.ReactNode;
}) {
  return <div className={`flex flex-col ${className || ''}`} data-id="element-253">
      <label className="text-[12px] font-medium text-slate-700 flex items-center mb-0.5" data-id="element-254">
        {label}
        {required && <span className="text-red-600 ml-1" data-id="element-255">*</span>}
      </label>
      {children}
      <div className="min-h-[0.75rem] mt-0.5" data-id="element-256">
        {error ? <span className="text-[10px] text-red-600 block leading-tight" data-id="element-257">
            {error}
          </span> : helperText ? <span className="text-[10px] text-slate-500 block leading-tight" data-id="element-258">
            {helperText}
          </span> : null}
      </div>
    </div>;
}
export function TextInput({
  value,
  onChange,
  placeholder,
  disabled,
  ...props
}: BaseInputProps & {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return <FieldWrapper {...props} data-id="element-259">
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} className={`h-8 px-2.5 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-260" />
    </FieldWrapper>;
}
export function NumberInput({
  value,
  onChange,
  placeholder,
  disabled,
  ...props
}: BaseInputProps & {
  value: number | '';
  onChange: (val: number | '') => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return <FieldWrapper {...props} data-id="element-261">
      <input type="number" value={value} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder={placeholder} disabled={disabled} className={`h-8 px-2.5 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-262" />
    </FieldWrapper>;
}
export function CurrencyInput({
  value,
  onChange,
  placeholder,
  disabled,
  ...props
}: BaseInputProps & {
  value: number | '';
  onChange: (val: number | '') => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return <FieldWrapper {...props} data-id="element-263">
      <div className="relative flex items-center" data-id="element-264">
        <span className="absolute left-2.5 text-slate-500 text-[12px] pointer-events-none" data-id="element-265">
          $
        </span>
        <input type="number" value={value} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder={placeholder} disabled={disabled} className={`h-8 w-full pl-6 pr-2.5 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-266" />
      </div>
    </FieldWrapper>;
}
export function PercentInput({
  value,
  onChange,
  placeholder,
  disabled,
  ...props
}: BaseInputProps & {
  value: number | '';
  onChange: (val: number | '') => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return <FieldWrapper {...props} data-id="element-267">
      <div className="relative flex items-center" data-id="element-268">
        <input type="number" step="0.001" value={value} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder={placeholder} disabled={disabled} className={`h-8 w-full pr-6 pl-2.5 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-269" />
        <span className="absolute right-2.5 text-slate-500 text-[12px] pointer-events-none" data-id="element-270">
          %
        </span>
      </div>
    </FieldWrapper>;
}
export function Select({
  value,
  onChange,
  options,
  disabled,
  ...props
}: BaseInputProps & {
  value: string;
  onChange: (val: string) => void;
  options: {
    label: string;
    value: string;
  }[];
  disabled?: boolean;
}) {
  const hasEmptyOption = options.some(opt => opt.value === '');
  return <FieldWrapper {...props} data-id="element-271">
      <div className="relative flex items-center" data-id="element-272">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className={`appearance-none h-8 w-full pl-2.5 pr-8 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 bg-white ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-273">
          {!hasEmptyOption && <option value="" disabled data-id="element-274">
              Select...
            </option>}
          {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-275">
              {opt.label}
            </option>)}
        </select>
        <ChevronDown className="absolute right-2 w-3.5 h-3.5 text-slate-400 pointer-events-none" data-id="element-276" />
      </div>
    </FieldWrapper>;
}
export function SegmentedControl({
  value,
  onChange,
  options,
  disabled,
  ...props
}: BaseInputProps & {
  value: string | boolean;
  onChange: (val: any) => void;
  options: {
    label: string;
    value: string | boolean;
  }[];
  disabled?: boolean;
}) {
  return <FieldWrapper {...props} data-id="element-277">
      <div className={`flex h-8 p-0.5 bg-slate-100 rounded border ${props.error ? 'border-red-500' : 'border-slate-200'}`} data-id="element-278">
        {options.map(opt => {
        const isSelected = value === opt.value;
        return <button key={String(opt.value)} type="button" disabled={disabled} onClick={() => onChange(opt.value)} className={`flex-1 flex items-center justify-center text-[12px] font-medium rounded-sm transition-all ${isSelected ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-279">
              {opt.label}
            </button>;
      })}
      </div>
    </FieldWrapper>;
}
export function TextArea({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 3,
  ...props
}: BaseInputProps & {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}) {
  return <FieldWrapper {...props} data-id="element-280">
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} rows={rows} className={`px-2.5 py-1.5 border rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 ${props.error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}`} data-id="element-281" />
    </FieldWrapper>;
}